import { appendFile, mkdir, open, readFile } from 'node:fs/promises'
import { dirname, join, resolve } from 'node:path'
import type { Context } from '@tnega/core'
import {
  BlackboardError,
  BlackboardService,
  FACT_KINDS,
  type FactCommit,
  type FactKind,
  type FactListOptions,
  type FactRecord,
} from '@tnega/blackboard'

export interface LocalBlackboardConfig {
  /**
   * Blackboard 目录。组合层决定它在 Project 目录下的位置
   * （约定为 `<project>/.tnega/projects/<projectId>/blackboard`），Provider 自己不
   * 解析 Project id，也就不存在把它拼进路径的风险。
   */
  root: string
}

const JOURNAL = 'journal.jsonl'

function factKey(kind: FactKind, id: string): string {
  return `${kind} ${id}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseRecord(value: unknown): FactRecord | undefined {
  if (!isRecord(value)) return undefined
  const { kind, id, seq, version, author, createdAt, updatedAt } = value
  const factKind = FACT_KINDS.find(candidate => candidate === kind)
  if (!factKind || typeof id !== 'string') return undefined
  if (typeof seq !== 'number' || !Number.isSafeInteger(seq)) return undefined
  if (typeof version !== 'number' || !Number.isSafeInteger(version) || version < 1) return undefined
  if (typeof author !== 'string') return undefined
  if (typeof createdAt !== 'number' || typeof updatedAt !== 'number') return undefined
  return {
    kind: factKind,
    id,
    seq,
    version,
    data: value.data,
    author,
    source: isRecord(value.source) ? value.source : {},
    createdAt,
    updatedAt,
    deleted: value.deleted === true,
  }
}

/** A newline commits one whole transaction; legacy records remain readable. */
function parseLine(line: string, terminated: boolean): FactRecord[] | undefined {
  let value: unknown
  try {
    value = JSON.parse(line)
  } catch {
    return undefined
  }
  if (isRecord(value) && value.type === 'blackboard/transaction' && value.version === 1) {
    if (!terminated || !Array.isArray(value.records) || value.records.length === 0) return undefined
    const records: FactRecord[] = []
    for (const item of value.records) {
      const record = parseRecord(item)
      if (!record) return undefined
      records.push(record)
    }
    return records
  }
  const record = parseRecord(value)
  return record ? [record] : undefined
}

/**
 * 本地 Blackboard Provider：一条只增的 JSONL 日志，内存里按 kind 折叠出当前版本。
 *
 * 取舍：
 *
 * - **一条 journal 而不是每种 kind 一个文件**。每次提交是一行完整事务，换行标记
 *   提交边界；恢复时只应用整批记录，信封与投递记录必须一起出现。
 * - **读全量、常驻内存**。Project 的共享事实是「索引级」数据（记忆条目、Agent 关系、
 *   产物引用），不含对话与文件正文；这与 `SessionLog` 读整份 JSONL 的取舍一致。
 * - **同一批内同一 kind/id 只允许出现一次**。两条记录都基于同一个「当前版本」判断，
 *   第二条会静默压掉第一条，因此直接拒绝而不是猜调用者的意图。
 */
export class LocalBlackboardService extends BlackboardService {
  private readonly file: string
  private readonly versions = new Map<string, FactRecord[]>()
  /** 每种 kind 的当前版本，按 id 索引；读取时按 `seq` 排序成变更流。 */
  private readonly current = new Map<FactKind, Map<string, FactRecord>>()
  private nextSeq = 1
  private loaded: Promise<void> | undefined
  private tail: Promise<unknown> = Promise.resolve()
  private journalSize = 0
  private recoveryFailure: BlackboardError | undefined

  constructor(ctx: Context, config: LocalBlackboardConfig) {
    super(ctx)
    if (!config?.root || typeof config.root !== 'string') {
      throw new BlackboardError('blackboard-local requires a root directory', 'BLACKBOARD_INVALID')
    }
    this.file = join(resolve(config.root), JOURNAL)
  }

  protected override async runRead(
    kind: FactKind,
    id: string,
  ): Promise<FactRecord | undefined> {
    await this.ensureLoaded()
    return this.versions.get(factKey(kind, id))?.at(-1)
  }

  protected override async runList(
    kind: FactKind,
    options: FactListOptions,
  ): Promise<FactRecord[]> {
    await this.ensureLoaded()
    const after = options.after ?? 0
    const records = [...(this.current.get(kind)?.values() ?? [])]
      .filter(record => record.seq > after && (options.includeDeleted === true || !record.deleted))
      .sort((a, b) => a.seq - b.seq)
    return options.limit === undefined ? records : records.slice(0, options.limit)
  }

  protected override async runHistory(kind: FactKind, id: string): Promise<FactRecord[]> {
    await this.ensureLoaded()
    return [...(this.versions.get(factKey(kind, id)) ?? [])]
  }

  protected override runCommit(input: FactCommit): Promise<FactRecord> {
    return this.run(() => {
      const [record] = this.plan([input])
      return this.persist([record!]).then(() => record!)
    })
  }

  protected override runCommitAll(inputs: readonly FactCommit[]): Promise<FactRecord[]> {
    return this.run(() => {
      const records = this.plan(inputs)
      return this.persist(records).then(() => records)
    })
  }

  /** 串行化每一次「读当前版本 → 校验 → 写」的转换。 */
  private run<T>(task: () => Promise<T>): Promise<T> {
    const result = this.tail.then(async () => {
      await this.ensureLoaded()
      return task()
    })
    this.tail = result.then(() => undefined, () => undefined)
    return result
  }

  private ensureLoaded(): Promise<void> {
    if (this.recoveryFailure) return Promise.reject(this.recoveryFailure)
    this.loaded ??= this.load()
    return this.loaded
  }

  private async load(): Promise<void> {
    let bytes: Buffer
    try {
      bytes = await readFile(this.file)
    } catch (error) {
      if (isRecord(error) && error.code === 'ENOENT') return
      throw new BlackboardError(
        `could not read blackboard journal ${this.file}`,
        'BLACKBOARD_FAILED',
        { cause: error },
      )
    }
    let offset = 0
    const recovered: FactRecord[] = []
    while (offset < bytes.length) {
      const newline = bytes.indexOf(10, offset)
      const terminated = newline !== -1
      const end = terminated ? newline : bytes.length
      const records = parseLine(bytes.subarray(offset, end).toString('utf8'), terminated)
      if (!terminated) {
        if (records) {
          // Old journals may contain a complete FactRecord without a delimiter.
          await appendFile(this.file, '\n', { encoding: 'utf8', flush: true })
          recovered.push(...records)
          offset = bytes.length + 1
        } else {
          await this.truncate(offset)
        }
        break
      }
      if (records) recovered.push(...records)
      offset = end + 1
    }
    this.journalSize = offset
    for (const record of recovered) this.apply(record)
  }

  private async truncate(size: number): Promise<void> {
    const file = await open(this.file, 'r+')
    try {
      await file.truncate(size)
      await file.sync()
    } finally {
      await file.close()
    }
  }

  private apply(record: FactRecord): void {
    const key = factKey(record.kind, record.id)
    const versions = this.versions.get(key)
    if (versions) versions.push(record)
    else this.versions.set(key, [record])
    const kind = this.current.get(record.kind)
    if (kind) kind.set(record.id, record)
    else this.current.set(record.kind, new Map([[record.id, record]]))
    this.nextSeq = Math.max(this.nextSeq, record.seq + 1)
  }

  private plan(inputs: readonly FactCommit[]): FactRecord[] {
    const now = Date.now()
    const seen = new Set<string>()
    const records: FactRecord[] = []
    for (const input of inputs) {
      const key = factKey(input.kind, input.id)
      if (seen.has(key)) {
        throw new BlackboardError(
          `duplicate fact in one commit: ${input.kind}/${input.id}`,
          'BLACKBOARD_INVALID',
        )
      }
      seen.add(key)
      const current = this.versions.get(key)?.at(-1)
      const expected = input.expectedVersion
      if (expected === null) {
        if (current && !current.deleted) {
          throw new BlackboardError(
            `fact already exists: ${input.kind}/${input.id}`,
            'BLACKBOARD_CONFLICT',
            { current },
          )
        }
      } else if (current) {
        if (expected === undefined) {
          throw new BlackboardError(
            `fact ${input.kind}/${input.id} exists; pass expectedVersion to update it`,
            'BLACKBOARD_VERSION_REQUIRED',
            { current },
          )
        }
        if (expected !== current.version) {
          throw new BlackboardError(
            `fact ${input.kind}/${input.id} is at version ${current.version}, not ${expected}`,
            'BLACKBOARD_CONFLICT',
            { current },
          )
        }
      } else if (expected !== undefined) {
        throw new BlackboardError(
          `fact does not exist: ${input.kind}/${input.id}`,
          'BLACKBOARD_NOT_FOUND',
        )
      }
      records.push({
        kind: input.kind,
        id: input.id,
        seq: 0,
        version: (current?.version ?? 0) + 1,
        data: input.data,
        author: input.author,
        source: input.source ?? {},
        createdAt: current?.createdAt ?? now,
        updatedAt: now,
        deleted: input.deleted === true,
      })
    }
    return records
  }

  private async persist(records: readonly FactRecord[]): Promise<void> {
    if (records.length === 0) return
    for (const [index, record] of records.entries()) record.seq = this.nextSeq + index
    const text = `${JSON.stringify({ type: 'blackboard/transaction', version: 1, records })}\n`
    await mkdir(dirname(this.file), { recursive: true })
    try {
      await appendFile(this.file, text, { encoding: 'utf8', flush: true })
    } catch (error) {
      try {
        await this.truncate(this.journalSize)
      } catch (recoveryError) {
        // Never append after an uncertain rollback: restart must repair the journal first.
        this.recoveryFailure = new BlackboardError(
          `could not recover blackboard journal ${this.file}; reopen the provider`,
          'BLACKBOARD_FAILED',
          { cause: recoveryError },
        )
      }
      throw new BlackboardError(
        `could not append to blackboard journal ${this.file}`,
        'BLACKBOARD_FAILED',
        { cause: error },
      )
    }
    this.journalSize += Buffer.byteLength(text)
    for (const record of records) this.apply(record)
  }
}

export const blackboardLocal = {
  name: 'blackboard-local',
  apply(ctx: Context, config: LocalBlackboardConfig): void {
    new LocalBlackboardService(ctx, config)
  },
}
